var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { HTTPImageryProvider } from "./HTTPImageryProvider";
import { TextureLoader } from "../loaders";
import { SRGBColorSpace } from "three";
import { WebMercatorTilingScheme } from "../../math/tiling";
export class BingMapsImageryProvider extends HTTPImageryProvider {
    constructor(metadata, attributions) {
        super(new WebMercatorTilingScheme(), {
            minLevel: metadata.zoomMin,
            maxLevel: metadata.zoomMax,
        });
        this._subdomainIndex = 0;
        if (metadata.imageUrl === null || metadata.imageUrl === undefined) {
            throw new Error("No 'imageUrl' in bing maps metadata");
        }
        if (metadata.imageUrlSubdomains === null || metadata.imageUrlSubdomains === undefined) {
            throw new Error("No 'imageUrlSubdomains' in bing maps metadata");
        }
        this._metadata = metadata;
        this._attributions = attributions;
        this._partiallyResolvedSubdomains = this._metadata.imageUrlSubdomains.map((v) => {
            // Pre-bake the subdomain
            const url = this._metadata.imageUrl.replace("{subdomain}", v);
            // Check if the URL already has the n query string set
            if (!url.includes("n=")) {
                // By using the power of undocumented (as far as I could find) API magic bullshit, the n=z parameter
                // tells the bing maps server to return an empty response when fetching missing tiles instead of the
                // crossed out camera dummy image. This allows us to reliably detect with a single request whether a
                // tile is available.
                //
                // I found this in cesium's source code, also can cite this issue on cesium's github
                // https://github.com/CesiumGS/cesium/issues/1353
                //
                // If the we're requesting is missing then the 0 sized response will bubble up the chain and throw
                // errors. Thank you bing maps.
                return `${url}&n=z`;
            }
            else {
                // SOMETIMES THEY ALREADY HAVE n=z IN THEIR URL WTF
                return url;
            }
        });
    }
    get attributions() {
        return this._attributions;
    }
    resolveTilePath(x, y, zoomLevel) {
        // const tileDimensions = WebMercator.tileDimensions(zoomLevel) - 1;
        // y = tileDimensions - y;
        // Alternate through each provided URL
        const subdomainIndex = this._subdomainIndex;
        this._subdomainIndex = (this._subdomainIndex + 1) % this._metadata.imageUrlSubdomains.length;
        // Convert our tile coordinate into bing map's quad key indexing system
        const quadKey = tileToQuadKey(x, y, zoomLevel);
        return this._partiallyResolvedSubdomains[subdomainIndex].replace("{quadkey}", quadKey);
    }
    loadTile(x, y, zoomLevel) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hasDataForTile(x, y, zoomLevel)) {
                throw new Error("Requested a tile that does not exist in the dataset bounds");
            }
            // Start at the target zoom level and walk back up the tree until we actually get a tile.
            //
            // Because bing maps is stupid, there's no clean way to detect the highest available zoom level at some
            // geographic position. The API *could* tell us, all the fields are there they just don't return
            // anything meaningful in them. This would be fine if the highest available zoom level didn't vary over
            // the globe, meaning we *have* to know the highest on a per-point basis so we can clamp to it instead
            // of loading garbage tiles.
            //
            // To work around this we use the spoooooooky power of undocumented API behavior. While I couldn't find
            // this anywhere in microsoft's documentation, adding "n=z" to the query string tells the tile server
            // to return nothing instead of a dummy tile. This allows us to simply and reliably know when a tile is
            // available without an extra network request.
            //
            // This allows us to provide a surprisingly optimal solution:
            // 1. Start at the initially requested tile
            // 2. If we get the tile we're done, just return it
            // 3. If we got an empty response we're outside the dataset, go back a zoom level and try to load the
            //    parent tile
            // 4. Repeat 2 to 3 until we successfully load a tile
            // 5. While this will almost certainly *never* happen in practice, it is theoretically possible for us
            //    to fail to load any tiles at all. In this case we just throw an exception as there's no sane way
            //    to handle this otherwise.
            //
            // This is surprisingly efficient as the most common case, requesting a tile that is available, only
            // requires the single network request to fetch that tile. We progressively get less and less efficient
            // the higher the disparity between the wanted zoom level and available zoom level, but in practice the
            // difference will be in the order of 1 or two zoom levels and only present when zoomed in very close
            // to the surface.
            let output = null;
            while (output === null) {
                try {
                    // Try to load the tile, this should throw if that tile doesn't have concrete data present
                    const texture = yield TextureLoader.load(this.resolveTilePath(x, y, zoomLevel), this.resolveRequestOptions());
                    // @ts-ignore
                    texture.colorSpace = SRGBColorSpace;
                    output = {
                        texture,
                        zoomLevel,
                        x,
                        y,
                    };
                }
                catch (e) {
                    // Update our coordinates to the parent tile and try again
                    zoomLevel = zoomLevel - 1;
                    x = Math.trunc(x / 2);
                    y = Math.trunc(y / 2);
                    if (zoomLevel < this.minLevel) {
                        throw new Error("No tiles available, wtf");
                    }
                }
            }
            return [output];
        });
    }
    resolveRequestOptions() {
        return undefined;
    }
}
const digits = ["0", "1", "2", "3"];
/* tslint:disable:no-bitwise */
/**
 * Converts tile XY coordinates into a QuadKey at a specified level of detail.
 * @constructor
 * @param zoomLevel
 * @param x
 * @param y
 */
function tileToQuadKey(x, y, zoomLevel) {
    let output = "";
    for (let i = zoomLevel; i > 0; i -= 1) {
        let dIdx = 0;
        const mask = 1 << (i - 1);
        if ((x & mask) !== 0) {
            dIdx++;
        }
        if ((y & mask) !== 0) {
            dIdx++;
            dIdx++;
        }
        output += digits[dIdx];
    }
    return output;
}
/* tslint:enable:no-bitwise */
